'use strict'

/**
 * Job Ads – branch / role selection options and combination-rule logic.
 *
 * SINGLE SOURCE OF TRUTH for both sides:
 *   - Cloud Functions:  require('./shared/job_ads_options')
 *   - Frontend:         re-exported by src/Methods/job_ads_options.js
 *
 * Keep this file pure CommonJS: no require(), no import/export syntax,
 * no Node or browser globals, no side effects.
 *
 * Data saved on a JobAds document (applyFormConfig.branches / applyFormConfig.roles):
 *   { enabled: bool, required: bool, options: [optionId], blockedPairs: ['a|b', ...] }
 * blockedPairs hold sorted, '|'-joined pair keys (Firestore rejects nested arrays).
 * For branches the keys pair REGION ids, for roles they pair ROLE ids.
 *
 * Data saved on a JobApplications document:
 *   selectedBranches: [{ id, label }], selectedRoles: [{ id, label }]
 */

const JOB_REGIONS = [
    { id: 'north',  label: 'צפון' },
    { id: 'haifa',  label: 'חיפה והקריות' },
    { id: 'sharon', label: 'שרון' },
    { id: 'center', label: 'מרכז' },
    { id: 'south',  label: 'דרום' }
]

// Canonical display order. Ids are stable ASCII slugs; labels may be edited freely.
const JOB_BRANCHES = [
    { id: 'big_regba',                label: 'ביג רגבה',                region: 'north'  },
    { id: 'karmiel',                  label: 'כרמיאל',                  region: 'north'  },
    { id: 'migdal_haemek',            label: 'מגדל העמק',               region: 'north'  },
    { id: 'afula',                    label: 'עפולה',                   region: 'north'  },
    { id: 'kiryat_motzkin_rabmad',    label: 'קריית מוצקין רבמ"ד',      region: 'haifa'  },
    { id: 'haifa_neve_shaanan',       label: 'חיפה – נווה שאנן',        region: 'haifa'  },
    { id: 'haifa_silver',             label: 'חיפה – סילבר',            region: 'haifa'  },
    { id: 'haifa_big_checkpost',      label: "חיפה – ביג צ'ק פוסט",     region: 'haifa'  },
    { id: 'haifa_halutzei_hataasiya', label: 'חיפה – חלוצי התעשייה',    region: 'haifa'  },
    { id: 'haifa_neviim',             label: 'חיפה – נביאים',           region: 'haifa'  },
    { id: 'haifa_herzl_city',         label: 'חיפה – הרצל סיטי',        region: 'haifa'  },
    { id: 'haifa_shuk',               label: 'חיפה – שוק',              region: 'haifa'  },
    { id: 'haifa_shuk_talpiot',       label: 'חיפה – שוק תלפיות',       region: 'haifa'  },
    { id: 'haifa_bat_galim',          label: 'חיפה – בת גלים',          region: 'haifa'  },
    { id: 'haifa_kiryat_eliezer',     label: 'חיפה – קריית אליעזר',     region: 'haifa'  },
    { id: 'haifa_sprinzak',           label: 'חיפה – שפרינצק',          region: 'haifa'  },
    { id: 'haifa_nitzan_kiryat_yam',  label: 'חיפה – ניצן / קריית ים',  region: 'haifa'  },
    { id: 'hadera',                   label: 'חדרה',                    region: 'sharon' },
    { id: 'netanya',                  label: 'נתניה',                   region: 'sharon' },
    { id: 'or_akiva',                 label: 'אור עקיבא',               region: 'sharon' },
    { id: 'bat_yam',                  label: 'בת ים',                   region: 'center' },
    { id: 'kiryat_gat',               label: 'קריית גת',                region: 'south'  }
]

const JOB_ROLES = [
    { id: 'cashier',   label: 'קופה' },
    { id: 'stocking',  label: 'סדרנות' },
    { id: 'deli',      label: 'מעדנייה' },
    { id: 'butcher',   label: 'קצבייה' },
    { id: 'warehouse', label: 'מחסן' }
]

const SELECTION_KINDS = ['branches', 'roles']

// Field name used on the JobApplications document for each kind
const SELECTION_FIELDS = { branches: 'selectedBranches', roles: 'selectedRoles' }

const QUESTIONS = {
    branches: { title: 'באילו סניפים תרצה/י לעבוד?', hint: 'ניתן לבחור יותר מאפשרות אחת.' },
    roles:    { title: 'באיזה תפקיד תרצה/י לעבוד?',  hint: 'ניתן לבחור יותר מאפשרות אחת.' }
}

const MESSAGES = {
    required:   'יש לבחור לפחות אפשרות אחת',
    notOffered: 'נבחרה אפשרות שאינה מוצעת במשרה זו',
    blocked:    function (a, b) { return 'לא ניתן לשלב בין ' + a + ' ל-' + b }
}

// ---------------------------------------------------------------------------
// Master-data helpers
// ---------------------------------------------------------------------------

function masterOf(kind) {
    if (kind === 'branches') return JOB_BRANCHES
    if (kind === 'roles') return JOB_ROLES
    return []
}

function findIn(list, id) {
    for (let i = 0; i < list.length; i++) {
        if (list[i].id === id) return list[i]
    }
    return null
}

/** Rule group of an option: branches → region id, roles → the role id itself. */
function groupOf(kind, id) {
    if (kind === 'branches') {
        const b = findIn(JOB_BRANCHES, id)
        return b ? b.region : null
    }
    return id
}

/** Display label of an option; falls back to the raw id (e.g. for removed options). */
function labelOf(kind, id) {
    const m = findIn(masterOf(kind), id)
    return m ? m.label : String(id)
}

/** Display label of a rule group (region label for branches, role label for roles). */
function groupLabelOf(kind, groupId) {
    if (kind === 'branches') {
        const r = findIn(JOB_REGIONS, groupId)
        return r ? r.label : String(groupId)
    }
    return labelOf(kind, groupId)
}

function uniqueStrings(arr) {
    const out = []
    const list = Array.isArray(arr) ? arr : []
    for (let i = 0; i < list.length; i++) {
        if (typeof list[i] === 'string' && out.indexOf(list[i]) === -1) out.push(list[i])
    }
    return out
}

// ---------------------------------------------------------------------------
// Config defaults / normalisation
// ---------------------------------------------------------------------------

/** The six original apply-form fields (previously duplicated in AdEditor and ApplyForm). */
function LEGACY_FIELD_DEFAULTS() {
    return {
        fullName: { enabled: true,  required: true  },
        phone:    { enabled: true,  required: true  },
        email:    { enabled: true,  required: false },
        idNumber: { enabled: false, required: false },
        message:  { enabled: true,  required: false },
        cv:       { enabled: true,  required: false }
    }
}

function DEFAULT_SELECTION_CONFIG() {
    return { enabled: false, required: false, options: [], blockedPairs: [] }
}

/** Fresh, type-safe copy of one selection config (branches or roles). */
function normalizeSelectionConfig(raw) {
    const cfg = DEFAULT_SELECTION_CONFIG()
    if (raw && typeof raw === 'object') {
        cfg.enabled = raw.enabled === true
        cfg.required = raw.required === true
        cfg.options = uniqueStrings(raw.options)
        cfg.blockedPairs = uniqueStrings(raw.blockedPairs)
    }
    return cfg
}

/**
 * Fresh, complete applyFormConfig: the six legacy keys plus `branches` and `roles`.
 * Missing keys get defaults, so ads saved before this feature existed keep working
 * (both selection questions default to disabled). Never mutates the input.
 */
function normalizeApplyFormConfig(raw) {
    const src = (raw && typeof raw === 'object') ? raw : {}
    const out = LEGACY_FIELD_DEFAULTS()
    Object.keys(out).forEach(function (key) {
        const v = src[key]
        if (v && typeof v === 'object') {
            out[key] = { enabled: v.enabled === true, required: v.required === true }
        }
    })
    SELECTION_KINDS.forEach(function (kind) {
        out[kind] = normalizeSelectionConfig(src[kind])
    })
    return out
}

// ---------------------------------------------------------------------------
// Combination rules
// ---------------------------------------------------------------------------

/** Canonical key for an unordered pair: pairKey('south','north') === 'north|south'. */
function pairKey(a, b) {
    const x = String(a)
    const y = String(b)
    return x < y ? x + '|' + y : y + '|' + x
}

function isPairBlocked(a, b, blockedPairs) {
    if (a == null || b == null || a === b) return false
    return Array.isArray(blockedPairs) && blockedPairs.indexOf(pairKey(a, b)) !== -1
}

/**
 * Availability of every offered option given the current selection.
 * A pure function of the selected SET, so click order never matters and
 * overlapping restrictions simply union (most restrictive wins).
 * A selected option is always available so it can be deselected.
 *
 * Returns { [optionId]: { available: boolean, blockedBy: [labels of selected blockers] } }
 */
function getOptionAvailability(selectedIds, cfg, kind) {
    const selected = uniqueStrings(selectedIds)
    const config = normalizeSelectionConfig(cfg)
    const result = {}
    config.options.forEach(function (id) {
        if (selected.indexOf(id) !== -1) {
            result[id] = { available: true, blockedBy: [] }
            return
        }
        const g = groupOf(kind, id)
        const blockedBy = selected
            .filter(function (sel) { return isPairBlocked(g, groupOf(kind, sel), config.blockedPairs) })
            .map(function (sel) { return labelOf(kind, sel) })
        result[id] = { available: blockedBy.length === 0, blockedBy: blockedBy }
    })
    return result
}

/**
 * Validates a candidate's selection against the ad's config.
 * Used identically by the browser form and by the Cloud Function.
 * Returns null when valid, otherwise a Hebrew error message.
 */
function validateSelection(selectedIds, cfg, kind) {
    const config = normalizeSelectionConfig(cfg)
    if (!config.enabled) return null
    const selected = uniqueStrings(selectedIds)

    for (let i = 0; i < selected.length; i++) {
        if (config.options.indexOf(selected[i]) === -1) return MESSAGES.notOffered
    }
    if (config.required && selected.length === 0) return MESSAGES.required
    for (let i = 0; i < selected.length; i++) {
        for (let j = i + 1; j < selected.length; j++) {
            if (isPairBlocked(groupOf(kind, selected[i]), groupOf(kind, selected[j]), config.blockedPairs)) {
                return MESSAGES.blocked(labelOf(kind, selected[i]), labelOf(kind, selected[j]))
            }
        }
    }
    return null
}

/**
 * Plain, persistence-safe copy of a selection config for saving on the ad:
 * booleans coerced, unknown option ids dropped, options in master order,
 * pair keys limited to two distinct groups that both have an offered option,
 * `required` forced off when the question is disabled.
 */
function sanitizeSelectionConfig(cfg, kind) {
    const config = normalizeSelectionConfig(cfg)
    const offered = masterOf(kind)
        .map(function (m) { return m.id })
        .filter(function (id) { return config.options.indexOf(id) !== -1 })

    const offeredGroups = {}
    offered.forEach(function (id) { offeredGroups[groupOf(kind, id)] = true })

    const pairs = []
    config.blockedPairs.forEach(function (key) {
        const parts = String(key).split('|')
        if (parts.length !== 2 || parts[0] === parts[1]) return
        if (!offeredGroups[parts[0]] || !offeredGroups[parts[1]]) return
        const k = pairKey(parts[0], parts[1])
        if (pairs.indexOf(k) === -1) pairs.push(k)
    })

    return {
        enabled: config.enabled,
        required: config.enabled && config.required,
        options: offered,
        blockedPairs: pairs
    }
}

/** [{ id, label }] for the given ids, in canonical order – stored on the application. */
function toLabelSnapshot(ids, kind) {
    const selected = uniqueStrings(ids)
    return masterOf(kind)
        .filter(function (m) { return selected.indexOf(m.id) !== -1 })
        .map(function (m) { return { id: m.id, label: m.label } })
}

// ---------------------------------------------------------------------------
// Display helpers
// ---------------------------------------------------------------------------

/**
 * Options grouped for display: [{ id, label, options: [{ id, label }] }].
 * Branches are grouped by region (empty regions omitted); roles form one unlabeled group.
 * Pass `optionIds` to restrict to the offered subset, or omit for the full master list.
 */
function groupedOptions(kind, optionIds) {
    const filter = Array.isArray(optionIds) ? optionIds : null
    const master = masterOf(kind).filter(function (m) { return !filter || filter.indexOf(m.id) !== -1 })
    const toOption = function (m) { return { id: m.id, label: m.label } }

    if (kind === 'branches') {
        return JOB_REGIONS
            .map(function (r) {
                return {
                    id: r.id,
                    label: r.label,
                    options: master.filter(function (m) { return m.region === r.id }).map(toOption)
                }
            })
            .filter(function (g) { return g.options.length > 0 })
    }
    return master.length ? [{ id: 'all', label: '', options: master.map(toOption) }] : []
}

/**
 * Items of the rule grid for the offered options:
 * regions having at least one offered branch, or the offered roles themselves.
 */
function ruleItems(kind, optionIds) {
    const offered = uniqueStrings(optionIds)
    if (kind === 'branches') {
        return JOB_REGIONS
            .filter(function (r) { return offered.some(function (id) { return groupOf('branches', id) === r.id }) })
            .map(function (r) { return { id: r.id, label: r.label } })
    }
    return JOB_ROLES
        .filter(function (r) { return offered.indexOf(r.id) !== -1 })
        .map(function (r) { return { id: r.id, label: r.label } })
}

module.exports = {
    JOB_REGIONS,
    JOB_BRANCHES,
    JOB_ROLES,
    SELECTION_KINDS,
    SELECTION_FIELDS,
    QUESTIONS,
    MESSAGES,
    LEGACY_FIELD_DEFAULTS,
    DEFAULT_SELECTION_CONFIG,
    normalizeSelectionConfig,
    normalizeApplyFormConfig,
    sanitizeSelectionConfig,
    uniqueStrings,
    pairKey,
    isPairBlocked,
    groupOf,
    labelOf,
    groupLabelOf,
    getOptionAvailability,
    validateSelection,
    toLabelSnapshot,
    groupedOptions,
    ruleItems
}
